// packages/frontend/src/app/providers/TelegramProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/** Narrow type for the Telegram Mini App surface we actually use. */
type MiniApp = {
  initData?: string;
  initDataUnsafe?: unknown;
  platform?: string;
  colorScheme?: 'light' | 'dark';
  isExpanded?: boolean;
  isClosingConfirmationEnabled?: boolean;
  isFullscreen?: boolean;
  viewportHeight?: number;
  viewportStableHeight?: number;
  ready?: () => void;
  expand?: () => void;
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
  requestFullscreen?: () => void; // Mini Apps 2.0
  onEvent?: (event: string, cb: (...args: any[]) => void) => void;
  offEvent?: (event: string, cb: (...args: any[]) => void) => void;
  openLink?: (url: string, opts?: { try_instant_view?: boolean }) => void;
  openTgLink?: (path_full: string) => void;
  setHeaderColor?: (color: 'bg_color' | string) => void;
  setBackgroundColor?: (color: string) => void;
};

type TelegramContextValue = {
  tg: MiniApp | undefined;                 // undefined when not running in Telegram
  initDataUnsafe: unknown;                 // exposed for consumers that need it
  platform: string;                        // 'android' | 'ios' | 'web' | 'unknown' …
  colorScheme: 'light' | 'dark' | undefined;
};

const TelegramContext = createContext<TelegramContextValue | undefined>(undefined);

export default function TelegramProvider({ children }: { children: React.ReactNode }) {
  const [value, setValue] = useState<TelegramContextValue>({
    tg: undefined,
    initDataUnsafe: undefined,
    platform: 'unknown',
    colorScheme: undefined,
  });

  useEffect(() => {
    // Guard for SSR and non-Telegram browsers
    const wa: MiniApp | undefined =
      typeof window !== 'undefined' ? (window as any)?.Telegram?.WebApp : undefined;

    if (!wa) {
      // Not in Telegram — still provide a stable context
      return;
    }

    try {
      // Signal readiness & expand to full height
      wa.ready?.();
      wa.expand?.();

      // Request fullscreen where supported (Mini Apps 2.0+)
      wa.requestFullscreen?.();

      // Keep closing confirmation disabled by default; enable only inside critical flows
      wa.disableClosingConfirmation?.();

      // Initialize context values
      setValue({
        tg: wa,
        initDataUnsafe: wa.initDataUnsafe,
        platform: wa.platform || 'unknown',
        colorScheme: wa.colorScheme,
      });

      // Subscribe to theme changes so consumers can react
      const handleTheme = () => {
        setValue((prev) => ({ ...prev, colorScheme: wa.colorScheme }));
      };
      wa.onEvent?.('themeChanged', handleTheme);

      return () => {
        wa.offEvent?.('themeChanged', handleTheme);
      };
    } catch {
      // Fail closed without breaking the app
    }
  }, []);

  const ctx = useMemo(() => value, [value]);

  return <TelegramContext.Provider value={ctx}>{children}</TelegramContext.Provider>;
}

export function useTelegram() {
  const ctx = useContext(TelegramContext);
  if (!ctx) throw new Error('useTelegram must be used within <TelegramProvider>');
  return ctx;
}
